import { CopyOutlined, DeleteOutlined, GiftOutlined, MinusOutlined, PlusOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Collapse, Divider, Drawer, Empty, InputNumber, List, message, Space, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../hooks/useAuth';
import { useI18n } from '../i18n';
import { apiService } from '../services/api';
import apiClient from '../utils/axiosConfig';
import CheckoutModal from './CheckoutModal';
import CouponSelector from './CouponSelector';

const { Title, Text } = Typography;

interface CartProps {
  onCheckout?: () => void;
  onCloseMobileMenu?: () => void;
  openCart?: boolean;
  onCartOpenChange?: (open: boolean) => void;
}

const Cart: React.FC<CartProps> = ({ onCheckout, onCloseMobileMenu, openCart, onCartOpenChange }) => {
  const { t } = useI18n();
  const { state, removeItem, updateQuantity, clearCart } = useCart();
  const { isAuthenticated, user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState<number[]>([]);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [availableCouponsCount, setAvailableCouponsCount] = useState(0);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [invitationCode, setInvitationCode] = useState<string>('');

  // 获取邀请码
  const fetchInvitationCode = async () => {
    try {
      const response = await apiClient.get('/auth/invitation-code');
      setInvitationCode(response.data.invitationCode);
    } catch (error) {
      console.error('获取邀请码失败:', error);
    }
  };

  // 获取可用优惠券数量
  const fetchAvailableCouponsCount = async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiService.getAvailableCoupons();
      if (response.success) {
        setAvailableCouponsCount(response.data.length);
      }
    } catch (error) {
      console.error('获取优惠券数量失败:', error);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAvailableCouponsCount();
      fetchInvitationCode();
    }
  }, [isAuthenticated]);

  // 监听外部控制购物车打开状态
  useEffect(() => {
    if (openCart !== undefined) {
      setVisible(openCart);
    }
  }, [openCart]);

  // 监听自定义事件打开购物车
  useEffect(() => {
    const handleOpenCart = () => {
      setVisible(true);
    };

    window.addEventListener('openCart', handleOpenCart);
    return () => {
      window.removeEventListener('openCart', handleOpenCart);
    };
  }, []);

  const handleQuantityChange = (productId: number, value: number | null) => {
    if (value && value > 0) {
      updateQuantity(productId, value);
    } else if (value === 0) {
      removeItem(productId);
    }
  };

  const handleRemoveItem = (productId: number) => {
    removeItem(productId);
    message.success(t('cart.removeItem'));
  };

  const handleClearCart = () => {
    clearCart();
    message.success(t('cart.clearCart'));
  };

  const handleCheckout = () => {
    if (state.items.length === 0) {
      message.warning(t('cart.empty'));
      return;
    }
    if (selectedItems.size === 0) {
      message.warning(t('cart.selectItemsWarning'));
      return;
    }
    setVisible(false);
    setCheckoutModalVisible(true);
    onCheckout?.();
  };

  // 处理优惠券选择变化
  const handleCouponSelectionChange = (couponIds: number[], discount: number) => {
    setSelectedCoupons(couponIds);
    setTotalDiscount(discount);
  };

  // 初始化选择状态 - 默认全选
  useEffect(() => {
    if (state.items.length > 0) {
      const allItemIds = new Set(state.items.map(item => item.product.id));
      setSelectedItems(allItemIds);
    }
  }, [state.items]);

  // 处理商品选择
  const handleItemSelect = (productId: number, checked: boolean) => {
    const newSelectedItems = new Set(selectedItems);
    if (checked) {
      newSelectedItems.add(productId);
    } else {
      newSelectedItems.delete(productId);
    }
    setSelectedItems(newSelectedItems);
  };

  // 全选/取消全选
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allItemIds = new Set(state.items.map(item => item.product.id));
      setSelectedItems(allItemIds);
    } else {
      setSelectedItems(new Set());
    }
  };

  // 计算选中商品的总价和数量
  const selectedItemsData = state.items.filter(item => selectedItems.has(item.product.id));
  const selectedTotalPrice = selectedItemsData.reduce((sum, item) => sum + (item.product.price || 0) * item.quantity, 0);
  const selectedTotalItems = selectedItemsData.reduce((sum, item) => sum + item.quantity, 0);

  // 计算最终总价（选中商品原价 - 优惠券折扣）
  const finalTotalPrice = Math.max(0, selectedTotalPrice - totalDiscount);

  const handleCheckoutSuccess = () => {
    setCheckoutModalVisible(false);
    message.success(t('cart.orderCreatedSuccess'));
  };

  return (
    <>
      <Button
        type="primary"
        icon={<ShoppingCartOutlined />}
        onClick={() => {
          setVisible(true);
          onCloseMobileMenu?.();
        }}
        style={{ position: 'relative' }}
      >
        {t('cart.title')}
        {state.totalItems > 0 && (
          <span
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#ff4d4f',
              color: 'white',
              borderRadius: '50%',
              width: '20px',
              height: '20px',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {state.totalItems}
          </span>
        )}
      </Button>

      <Drawer
        title={t('cart.title')}
        placement="right"
        onClose={() => {
          setVisible(false);
          onCartOpenChange?.(false);
        }}
        open={visible}
        width={400}
        footer={
          state.items.length > 0 ? (
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* 全选控制 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Checkbox
                  checked={selectedItems.size === state.items.length && state.items.length > 0}
                  indeterminate={selectedItems.size > 0 && selectedItems.size < state.items.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                >
                  {t('cart.selectAll')}
                </Checkbox>
                <Text type="secondary">
                  {t('cart.selectedItems', { selected: selectedItems.size, total: state.items.length })}
                </Text>
              </div>

              {/* 选中商品原价 */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text>{t('cart.selectedTotalPrice')}: NT${selectedTotalPrice.toFixed(2)}</Text>
                <Text type="secondary">{selectedTotalItems} {t('cart.quantity')}</Text>
              </div>

              {/* 优惠券折扣 */}
              {totalDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Text type="success">{t('cart.couponDiscount')}: -NT${totalDiscount.toFixed(2)}</Text>
                </div>
              )}

              {/* 最终总价 */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid #f0f0f0',
                paddingTop: '8px'
              }}>
                <Text strong style={{ fontSize: '16px', color: '#ff4d4f' }}>
                  {t('cart.payableAmount')}: NT${finalTotalPrice.toFixed(2)}
                </Text>
              </div>

              <Space style={{ width: '100%' }}>
                <Button onClick={handleClearCart} danger>
                  {t('cart.clear')}
                </Button>
                <Button
                  type="primary"
                  onClick={handleCheckout}
                  style={{ flex: 1 }}
                  disabled={selectedItems.size === 0}
                >
                  {t('cart.checkout')} ({selectedItems.size}{t('cart.items')})
                </Button>
              </Space>
            </Space>
          ) : null
        }
      >
        {state.items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('cart.empty')}
            style={{ marginTop: '50px' }}
          />
        ) : (
          <>
            <List
              dataSource={state.items}
              renderItem={(item) => (
                <List.Item>
                  <Card size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {/* 选择框 */}
                      <Checkbox
                        checked={selectedItems.has(item.product.id)}
                        onChange={(e) => handleItemSelect(item.product.id, e.target.checked)}
                      />

                      <img
                        src={item.product.imageUrl || '/img/placeholder.png'}
                        alt={item.product.name}
                        style={{
                          width: '60px',
                          height: '60px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/img/placeholder.png';
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Title level={5} style={{ margin: 0, fontSize: '14px' }}>
                          {item.product.name}
                        </Title>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          SKU: {item.product.sku}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                          <Text strong style={{ color: '#ff4d4f' }}>
                            NT${(item.product.price || 0).toFixed(2)}
                          </Text>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <Space.Compact>
                          <Button
                            size="small"
                            icon={<MinusOutlined />}
                            onClick={() => handleQuantityChange(item.product.id, item.quantity - 1)}
                          />
                          <InputNumber
                            size="small"
                            min={1}
                            max={item.product.stock}
                            value={item.quantity}
                            onChange={(value) => handleQuantityChange(item.product.id, value)}
                            style={{ width: '60px' }}
                          />
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={() => handleQuantityChange(item.product.id, item.quantity + 1)}
                          />
                        </Space.Compact>
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveItem(item.product.id)}
                        />
                      </div>
                    </div>
                    <Divider style={{ margin: '8px 0' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t('cart.subtotal')}: NT${((item.product.price || 0) * item.quantity).toFixed(2)}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {t('cart.stock')}: {item.product.stock}
                      </Text>
                    </div>
                  </Card>
                </List.Item>
              )}
            />

            {/* 优惠券选择区域 */}
            <Divider />
            <Collapse
              ghost
              size="small"
              defaultActiveKey={['coupons']}
              items={[
                {
                  key: 'coupons',
                  label: (
                    <Space>
                      <GiftOutlined />
                      <Text strong>{t('cart.useCoupon')}</Text>
                      {availableCouponsCount > 0 && (
                        <span
                          style={{
                            background: '#ff4d4f',
                            color: 'white',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            fontSize: '10px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                          }}
                        >
                          {availableCouponsCount}
                        </span>
                      )}
                      {totalDiscount > 0 && (
                        <Text type="success" style={{ fontSize: '12px' }}>
                          {t('cart.discounted')} NT${totalDiscount.toFixed(2)}
                        </Text>
                      )}
                    </Space>
                  ),
                  children: (
                    <div style={{ padding: '0 16px' }}>
                      {/* 邀请码显示 */}
                      {isAuthenticated && invitationCode && (
                        <div style={{ marginBottom: '16px', padding: '12px', background: '#f0f8ff', borderRadius: '8px', border: '1px solid #d9ecff' }}>
                          <Text strong style={{ fontSize: '14px', color: '#1890ff' }}>{t('messages.invitation.myInvitationCode')}</Text>

                          <Text code style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                            {invitationCode}
                          </Text>
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              // Check if clipboard API is available
                              if (navigator.clipboard && navigator.clipboard.writeText) {
                                navigator.clipboard.writeText(invitationCode).then(() => {
                                  message.success(t('messages.invitation.invitationCodeCopied'));
                                }).catch(() => {
                                  message.error(t('messages.invitation.copyFailed'));
                                });
                              } else {
                                // Fallback for older browsers
                                const textArea = document.createElement('textarea');
                                textArea.value = invitationCode;
                                textArea.style.position = 'fixed';
                                textArea.style.opacity = '0';
                                document.body.appendChild(textArea);
                                textArea.select();
                                try {
                                  document.execCommand('copy');
                                  message.success(t('messages.invitation.invitationCodeCopied'));
                                } catch (err) {
                                  message.error(t('messages.invitation.copyFailed'));
                                }
                                document.body.removeChild(textArea);
                              }
                            }}
                          />
                          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                            {t('messages.invitation.inviteFriendsHint')}
                          </Text>
                        </div>
                      )}
                      <CouponSelector
                        selectedCoupons={selectedCoupons}
                        onCouponSelectionChange={handleCouponSelectionChange}
                        totalAmount={state.totalPrice}
                      />
                    </div>
                  ),
                },
              ]}
            />
          </>
        )}
      </Drawer>

      <CheckoutModal
        visible={checkoutModalVisible}
        onClose={() => setCheckoutModalVisible(false)}
        onSuccess={handleCheckoutSuccess}
        preSelectedCoupons={selectedCoupons}
        preSelectedDiscount={totalDiscount}
        selectedItems={selectedItems}
      />
    </>
  );
};

export default Cart;
